import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHeatmap, useMe } from '../../api'
import {
  HeatmapHelpButton,
  HeatmapHelpModal,
} from '../../components/heatmap/HeatmapHelpModal'
import { HeatmapLegend } from '../../components/heatmap/HeatmapLegend'
import { LiveToggle } from '../../components/heatmap/LiveToggle'
import { PhoneGrid } from '../../components/heatmap/PhoneGrid'
import { RelayDownBanner } from '../../components/heatmap/RelayDownBanner'
import { ChartSkeleton } from '../../components/stats/StatSkeleton'
import { buildCells, countInCall, countOverMinute } from '../../lib/heatmapCells'

/** Top-of-board indicator — a count with its label. */
function Indicator({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100">
        {value}
      </span>
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
    </div>
  )
}

/**
 * Gate the inner body on `me` resolving — `useHeatmap` is admin-only,
 * so kicking off the request before we know we're admin would
 * unnecessarily 401 on operator-cached state during initial nav.
 */
export function PhonesHeatmapPage() {
  const { data: me } = useMe()
  if (!me) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <ChartSkeleton />
      </div>
    )
  }
  return <PhonesHeatmapPageInner />
}

function PhonesHeatmapPageInner() {
  // Snapshot on entry — LIVE polling is opt-in so the page doesn't burn
  // bandwidth in a background tab the operator forgot to close.
  const [live, setLive] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const { data, isLoading } = useHeatmap({ live })
  const navigate = useNavigate()

  const isRelayDown = data?.relay_online === false
  const phones = data?.phones ?? []
  // The whole fleet, sorted by name — every phone keeps a fixed grid
  // slot, so starting/ending a call never reshuffles the board.
  const cells = buildCells(phones)

  return (
    // h-full + overflow-hidden: the board fills the content area exactly,
    // the page never scrolls (PhoneGrid scrolls internally if it ever
    // has to). AuthLayout's <main> is already flex-1 — no layout change.
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-4 pt-5 pb-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-1.5">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Тепловая карта телефонов
            </h2>
            <HeatmapHelpButton onClick={() => setHelpOpen(true)} />
          </div>
          <Indicator label="В звонке" value={countInCall(phones)} />
          <Indicator label="> 1 мин" value={countOverMinute(phones)} />
        </div>
        <LiveToggle live={live} onToggle={() => setLive((v) => !v)} />
      </div>

      {isRelayDown && (
        <div className="shrink-0 px-4 pb-3">
          <RelayDownBanner />
        </div>
      )}

      {/* Board panel — always dark: it's a wall-display, the dashboard
          cards are dark tiles regardless of the app theme. */}
      <section className="mx-4 mb-4 flex min-h-0 flex-1 flex-col gap-3 rounded-xl border border-gray-800 bg-gray-950 p-3">
        {isLoading && !data ? (
          <div className="flex flex-1 items-center justify-center">
            <ChartSkeleton />
          </div>
        ) : cells.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
            Телефоны не добавлены
          </div>
        ) : (
          <>
            {/* Click a tile → call history pre-filtered to that phone. */}
            <PhoneGrid
              cells={cells}
              onSelect={(id) => navigate(`/staff/calls?phone=${id}`)}
            />
            <HeatmapLegend />
          </>
        )}
      </section>

      <HeatmapHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}
